import { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Heading, Skeleton, VStack, HStack, Fade, Text } from '@chakra-ui/react';
import axiosInstance from '../../services/axios';
import Banner from '../Slider/Banner';
import Cardslider from '../Slider/Cardslider';
import BrowseArtistCard from './BrowseArtistCard';
import BrowseCard from './BrowseCard';
import { useCart } from '../../context/CartContext';
import Trending from './Trending';

export default function Home() {
  const isMounted = useRef(false);
  const [topTracks, setTopTracks] = useState([]);
  const [topArtists, setTopArtists] = useState([]);
  const [featuredTracks, setFeaturedTracks] = useState([]);
  const [newReleases, setNewReleases] = useState([]);
  const [loadingTopTracks, setLoadingTopTracks] = useState(true);
  const [loadingTopArtists, setLoadingTopArtists] = useState(true);
  const [loadingFeatured, setLoadingFeatured] = useState(true);
  const [loadingNewReleases, setLoadingNewReleases] = useState(true);

  const { addToCart } = useCart();


  // Skeleton components
  const CardSkeleton = () => (
    <Box 
      maxW="300px" 
      minW="280px"
      w="full"
      p={3} 
      bg="transparent" 
      border="2px" 
      borderColor="dark.500" 
      borderRadius="lg" 
      m={2}
    >
      <Box p={3}>
        <Skeleton height="200px" borderRadius="lg" startColor="gray.100" endColor="gray.300" />
        <VStack mt={6} spacing={3} align="stretch">
          <Skeleton height="16px" startColor="gray.100" endColor="gray.300" />
          <Skeleton height="12px" width="60px" startColor="gray.100" endColor="gray.300" />
          <HStack justify="space-between">
            <Skeleton height="20px" width="60px" startColor="gray.100" endColor="gray.300" />
            <HStack>
              <Skeleton height="32px" width="32px" borderRadius="md" startColor="gray.100" endColor="gray.300" />
              <Skeleton height="32px" width="32px" borderRadius="md" startColor="gray.100" endColor="gray.300" />
            </HStack>
          </HStack>
          <HStack>
            <Skeleton height="20px" width="20px" borderRadius="full" startColor="gray.100" endColor="gray.300" />
            <Skeleton height="20px" width="20px" borderRadius="full" startColor="gray.100" endColor="gray.300" />
            <Skeleton height="20px" width="20px" borderRadius="full" startColor="gray.100" endColor="gray.300" />
          </HStack>
        </VStack>
      </Box>
    </Box>
  );

  const SliderSkeleton = ({ count = 4 }) => (
    <Box p={3}>
      <HStack 
        spacing={2} 
        overflowX="auto"
        pb={4}
      >
        {Array.from({ length: count }).map((_, index) => (
          <CardSkeleton key={index} />
        ))}
      </HStack>
    </Box>
  );

  // Removed - banner now shows featured tracks only

  const fetchTopTracks = useCallback(async () => {
    try {
      setLoadingTopTracks(true);
      const response = await axiosInstance.get('/statistics/get_top_tracks');
      setTopTracks(response.data || []);
    } catch (error) {
      console.error('Error fetching top tracks:', error);
      setTopTracks([]);
    } finally {
      setLoadingTopTracks(false);
    }
  }, []);

  const fetchTopArtists = useCallback(async () => {
    try {
      setLoadingTopArtists(true);
      const response = await axiosInstance.get('/statistics/get_top_artists');
      setTopArtists(response.data || []);
    } catch (error) {
      console.error('Error fetching top artists:', error);
      setTopArtists([]);
    } finally {
      setLoadingTopArtists(false);
    }
  }, []);

  const fetchFeaturedTracks = useCallback(async () => {
    try {
      setLoadingFeatured(true);
      const response = await axiosInstance.get('/featured/featured-tracks');
      // Extract tracks from the featured tracks response
      // Each feature object has a nested track property
      const tracks = (response.data || []).map(feature => feature.track);
      setFeaturedTracks(tracks);
    } catch (error) {
      console.error('Error fetching featured tracks:', error);
      setFeaturedTracks([]);
    } finally {
      setLoadingFeatured(false);
    }
  }, []);

  const fetchNewReleases = useCallback(async () => {
    try {
      setLoadingNewReleases(true);
      const response = await axiosInstance.get('/statistics/get_latest_tracks');
      setNewReleases(response.data || []);
    } catch (error) {
      console.error('Error fetching new releases:', error);
      setNewReleases([]);
    } finally {
      setLoadingNewReleases(false);
    }
  }, []);

  useEffect(() => {
    if (isMounted.current) return;

    fetchTopTracks();
    fetchTopArtists();
    fetchFeaturedTracks();
    fetchNewReleases();

    isMounted.current = true;
  }, [fetchTopTracks, fetchTopArtists, fetchFeaturedTracks, fetchNewReleases]);

  return (
    <Box w="100%" overflow="hidden">
      <Box p={3}>
        <Heading size={{ base: "sm", md: "md" }} textAlign="center" color="dark.900" borderRadius={3}>
          This is a demonstration version of BeatStake. Artist profiles shown here are for illustrative purposes only. The artists have not signed up, endorsed, or partnered with BeatStake.
        </Heading>

      </Box>
      {loadingFeatured ? (
        <Box p={3}>
          <Skeleton height="300px" borderRadius="lg" />
        </Box>
      ) : featuredTracks.length > 0 ? (
        <Fade in={!loadingFeatured}>
          <Box>
            <Heading size="lg" color="dark.900" p={{base:"1rem",md:"2rem",lg:"3rem"}} borderRadius={3}>
              Featured Tracks
            </Heading>
            <Banner items={featuredTracks} />
          </Box>
        </Fade>
      ) : null}

      <Box>
        <Heading size="lg" color="dark.900" p={{base:"1rem",md:"2rem",lg:"3rem"}} borderRadius={3}>
          Trending
        </Heading>
        <Trending />
      </Box>

      <Box>
        <Heading size="lg" color="dark.900" p={{base:"1rem",md:"2rem",lg:"3rem"}} borderRadius={3}>
          Top Music
        </Heading>
        {loadingTopTracks ? (
          <SliderSkeleton count={4} />
        ) : topTracks.length > 0 ? (
          <Fade in={!loadingTopTracks}>
            <Cardslider key={`top-tracks-${topTracks.length}`}>
              {topTracks.map((trackData, index) => (
                <BrowseCard
                  key={trackData.track.track_id || index}
                  track={trackData.track}
                  handleClick={() => addToCart(trackData.track)}
                />
              ))}
            </Cardslider>
          </Fade>
        ) : (
          <Box p={8} textAlign="center">
            <Heading size="sm" color="gray.500" mb={2}>
              No top tracks available yet
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Check back later for trending music
            </Text>
          </Box>
        )}
      </Box>
      <Box>
        <Heading size="lg" color="dark.900" p={{base:"1rem",md:"2rem",lg:"3rem"}} borderRadius={3}>
          New Releases
        </Heading>
        {loadingNewReleases ? (
          <SliderSkeleton count={4} />
        ) : newReleases.length > 0 ? (
          <Fade in={!loadingNewReleases}>
            <Cardslider key={`new-releases-${newReleases.length}`}>
              {newReleases.map((track, index) => (
                <BrowseCard
                  key={track.track_id || index}
                  track={track}
                  handleClick={() => addToCart(track)}
                />
              ))}
            </Cardslider>
          </Fade>
        ) : (
          <Box p={8} textAlign="center">
            <Heading size="sm" color="gray.500" mb={2}>
              No new releases available yet
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Check back later for new music
            </Text>
          </Box>
        )}
      </Box>

      <Box>
        <Heading size="lg" color="dark.900" p={{base:"1rem",md:"2rem",lg:"3rem"}} borderRadius={3}>
          Top Artists
        </Heading>
        {loadingTopArtists ? (
          <SliderSkeleton count={4} />
        ) : topArtists.length > 0 ? (
          <Fade in={!loadingTopArtists}>
            <Cardslider key={`top-artists-${topArtists.length}`}>
              {topArtists.map((artistData, index) => (
                <BrowseArtistCard
                  key={artistData.artist.user_id || index}
                  artist={artistData.artist}
                />
              ))}
            </Cardslider>
          </Fade>
        ) : (
          <Box p={8} textAlign="center">
            <Heading size="sm" color="gray.500" mb={2}>
              No top artists available yet
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Check back later for trending artists
            </Text>
          </Box>
        )}
      </Box>

    </Box>
  );
}